import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Users, Wifi, Tv, ShowerHead, Wind, Coffee } from 'lucide-react'
import { SITE_NAME } from '@/lib/constants'
import { getImageUrl } from '@/lib/image-url'

/* ─── Room data matching hbnuevavalladolid.com/habitaciones exactly ─── */
const ROOMS = [
  {
    key: 'deluxe-queen',
    slug: 'deluxe-queen',
    name: 'Deluxe Queen',
    capacity: '2 personas',
    bathroom: 'Baño privado',
    bg: 'navy',
    description:
      'La habitación Deluxe Queen combina la elegancia moderna con toques coloniales para ofrecer una experiencia única de lujo y confort. Con una cama queen tamaño generoso, el espacio está decorado con muebles contemporáneos, manteniendo detalles arquitectónicos coloniales como vigas de madera expuestas. La iluminación suave y los colores cálidos crean un ambiente acogedor, mientras que las comodidades incluyen un escritorio ergonómico, Wi-Fi de alta velocidad, televisión de pantalla plana, y un baño privado con acabados mediterráneos.',
    image: '/images/nuva/rooms/room-deluxe-queen-hero.jpeg',
  },
  {
    key: 'deluxe-queen-balcony',
    slug: 'deluxe-queen-balcony',
    name: 'Deluxe Queen Balcony',
    capacity: '2 personas',
    bathroom: 'Baño privado',
    bg: 'white',
    description:
      'La habitación Deluxe Queen Balcony eleva la experiencia con acceso a un balcón privado para disfrutar del ambiente histórico de Comayagua. Los pisos de baldosa artesanal y las vigas de madera expuestas evocan la arquitectura colonial de la ciudad. Incluye escritorio ergonómico, Wi-Fi de alta velocidad, televisión de pantalla plana, y un baño privado con elegantes acabados de mármol.',
    image: '/images/nuva/rooms/room-deluxe-queen-balcony-hero.jpeg',
  },
  {
    key: 'junior-suite-king',
    slug: 'junior-suite-king',
    name: 'Junior Suite King',
    capacity: '2 personas',
    bathroom: 'Baño privado',
    bg: 'navy',
    description:
      'La Junior Suite con cama King ofrece una sofisticada combinación de estilo moderno y colonial, creando un espacio amplio y acogedor. La habitación cuenta con una cama king que garantiza el máximo confort, detalles coloniales como techos altos con vigas de madera y suelos de cerámica moderna. El área de estar separada añade un toque de lujo, perfecta para relajarse o trabajar. Wi-Fi de alta velocidad, televisión de pantalla plana y un elegante baño privado con acabados coloniales.',
    image: '/images/nuva/rooms/room-junior-suite-king-hero.jpeg',
  },
  {
    key: 'doble-queen',
    slug: 'doble-queen',
    name: 'Doble Queen',
    capacity: '4 personas',
    bathroom: 'Baño privado',
    bg: 'white',
    description:
      'La habitación Doble Queen combina un estilo moderno con encantadores detalles coloniales, ofreciendo un espacio amplio y elegante. Las camas queen están rodeadas de una decoración contemporánea que contrasta con elementos tradicionales como techos con vigas de madera expuestas y suelos de cerámica moderna. Con amplios ventanales que permiten la entrada de luz natural, Wi-Fi de alta velocidad, una pantalla plana y un baño privado con acabados coloniales.',
    image: '/images/nuva/rooms/room-doble-queen-hero.jpeg',
  },
  {
    key: 'master-suite',
    slug: 'master-suite',
    name: 'Master Suite',
    capacity: '4 personas',
    bathroom: 'Baño privado',
    bg: 'navy',
    description:
      'La Master Suite es el epítome del lujo y el confort, diseñada para quienes buscan una experiencia inigualable. Con un amplio dormitorio que incluye dos camas queen y una elegante área de estar, esta suite destaca por su estilo contemporáneo con toques coloniales, como techos altos con vigas de madera y detalles artesanales. La suite cuenta con un baño espacioso equipado con todo lo necesario, Wi-Fi de alta velocidad, televisión de pantalla plana, minibar y vistas panorámicas.',
    image: '/images/nuva/rooms/room-master-suite-hero.jpeg',
  },
]

/* ─── Amenity icons row ─── */
function AmenityBadge({ icon, label, onDark }: { icon: React.ReactNode; label: string; onDark?: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={onDark ? 'text-white/80' : 'text-[#0F2966]/70'}>{icon}</span>
      <span className={`text-xs font-semibold uppercase tracking-wide ${onDark ? 'text-white/80' : 'text-[#666666]'}`}>
        {label}
      </span>
    </div>
  )
}

/* ─── Single room detail row (matching real site 2-col layout) ─── */
function RoomRow({ room, index }: { room: typeof ROOMS[number]; index: number }) {
  const isNavy = room.bg === 'navy'

  return (
    <section
      className="w-full"
      style={{ backgroundColor: isNavy ? '#0F2966' : '#FFFFFF' }}
      id={room.key}
    >
      <div
        className="mx-auto grid items-center"
        style={{
          maxWidth: '1280px',
          paddingTop: '50px',
          paddingBottom: '50px',
          paddingLeft: '80px',
          paddingRight: '80px',
          gridTemplateColumns: '1fr 2fr',
          gap: '48px',
        }}
      >
        {/* Arch image column */}
        <div
          style={{
            borderRadius: '300px 300px 0px 0px',
            overflow: 'hidden',
            aspectRatio: '3 / 4',
            flexShrink: 0,
          }}
        >
          <img
            src={getImageUrl(room.image)}
            alt={room.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
            loading={index === 0 ? 'eager' : 'lazy'}
          />
        </div>

        {/* Text column */}
        <div style={{ paddingTop: '20px', paddingLeft: '30px', paddingBottom: '20px' }}>
          {/* Room name */}
          <h2
            className="font-heading"
            style={{
              fontSize: '23px',
              fontWeight: 700,
              textTransform: 'uppercase',
              color: isNavy ? '#FFFFFF' : '#000000',
              marginBottom: '12px',
            }}
          >
            {room.name}
          </h2>

          {/* Capacity line */}
          <p
            className="font-heading"
            style={{
              fontSize: '14px',
              fontWeight: 700,
              color: isNavy ? '#F3EADB' : '#0F2966',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '4px',
            }}
          >
            CAPACIDAD MÁXIMA:{' '}
            <span style={{ fontWeight: 700 }}>{room.capacity.toUpperCase()}</span>
          </p>
          <p
            className="font-heading"
            style={{
              fontSize: '14px',
              fontWeight: 700,
              color: isNavy ? '#F3EADB' : '#0F2966',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '20px',
            }}
          >
            BAÑO: <span style={{ fontWeight: 700 }}>{room.bathroom.toUpperCase()}</span>
          </p>

          {/* Amenity icons */}
          <div className="flex flex-wrap gap-x-5 gap-y-2 mb-5">
            <AmenityBadge icon={<Wifi size={14} />} label="Wi-Fi" onDark={isNavy} />
            <AmenityBadge icon={<Tv size={14} />} label="TV Pantalla Plana" onDark={isNavy} />
            <AmenityBadge icon={<ShowerHead size={14} />} label="Agua Caliente" onDark={isNavy} />
            <AmenityBadge icon={<Wind size={14} />} label="Aire Acondicionado" onDark={isNavy} />
            <AmenityBadge icon={<Coffee size={14} />} label="Escritorio de Trabajo" onDark={isNavy} />
            <AmenityBadge icon={<Users size={14} />} label={room.capacity} onDark={isNavy} />
          </div>

          {/* Description */}
          <p
            style={{
              fontSize: '15px',
              lineHeight: 1.75,
              color: isNavy ? 'rgba(255,255,255,0.85)' : '#666666',
              marginBottom: '28px',
              maxWidth: '560px',
            }}
          >
            {room.description}
          </p>

          {/* CTA button */}
          <Link
            to={`/rooms/${room.slug}`}
            className="inline-flex items-center justify-center font-heading font-bold uppercase tracking-wide transition-colors duration-200"
            style={{
              width: '200px',
              height: '50px',
              borderRadius: '100px',
              fontSize: '13px',
              letterSpacing: '0.08em',
              backgroundColor: isNavy ? 'transparent' : '#0F2966',
              color: '#FFFFFF',
              border: isNavy ? '1px solid #FFFFFF' : '1px solid #0F2966',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget
              el.style.backgroundColor = '#F3EADB'
              el.style.color = '#0F2966'
              el.style.borderColor = '#F3EADB'
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget
              el.style.backgroundColor = isNavy ? 'transparent' : '#0F2966'
              el.style.color = '#FFFFFF'
              el.style.borderColor = isNavy ? '#FFFFFF' : '#0F2966'
            }}
          >
            VER DISPONIBILIDAD
          </Link>
        </div>
      </div>
    </section>
  )
}

/* ─── Policies section ─── */
function PoliciesSection() {
  return (
    <section style={{ backgroundColor: '#F2F2F2', padding: '50px 40px 100px' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        {/* Heading */}
        <h2
          className="font-heading"
          style={{
            fontSize: '28px',
            fontWeight: 700,
            textTransform: 'uppercase',
            color: '#0F2966',
            textAlign: 'center',
            marginBottom: '24px',
          }}
        >
          Políticas de Reservas
        </h2>

        {/* Terms CTA */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <a
            href="/terminos-y-condiciones"
            className="inline-flex items-center justify-center font-heading font-bold uppercase tracking-wide transition-colors duration-200"
            style={{
              width: '278px',
              height: '40px',
              borderRadius: '100px',
              fontSize: '12px',
              letterSpacing: '0.08em',
              backgroundColor: '#0F2966',
              color: '#FFFFFF',
              border: '1px solid #0F2966',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget
              el.style.backgroundColor = '#F3EADB'
              el.style.color = '#0F2966'
              el.style.borderColor = '#F3EADB'
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget
              el.style.backgroundColor = '#0F2966'
              el.style.color = '#FFFFFF'
              el.style.borderColor = '#0F2966'
            }}
          >
            VER TÉRMINOS Y CONDICIONES
          </a>
        </div>

        {/* Two policy cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '30px',
          }}
        >
          {/* Horarios card */}
          <div
            style={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #EEE7E0',
              borderRadius: '300px 300px 0px 0px',
              padding: '45px 25px 20px',
              textAlign: 'center',
            }}
          >
            <h3
              className="font-heading"
              style={{
                fontSize: '24px',
                fontWeight: 700,
                textTransform: 'uppercase',
                color: '#0F2966',
                marginBottom: '16px',
              }}
            >
              HORARIOS
            </h3>
            <p style={{ fontSize: '15px', lineHeight: 1.8, color: '#666666' }}>
              La recepción de nuestro hotel se encuentra abierta las 24hs del día, los 365 días del año.
              <br /><br />
              El Check-in es a las <strong>2:00 PM</strong> y el Check-Out a las <strong>11:00 AM</strong>, consulte por la posibilidad de late check-out en recepción.
            </p>
          </div>

          {/* Cancelación card */}
          <div
            style={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #EEE7E0',
              borderRadius: '300px 300px 0px 0px',
              padding: '45px 25px 20px',
              textAlign: 'center',
            }}
          >
            <h3
              className="font-heading"
              style={{
                fontSize: '24px',
                fontWeight: 700,
                textTransform: 'uppercase',
                color: '#0F2966',
                marginBottom: '16px',
              }}
            >
              POLÍTICA DE CANCELACIÓN
            </h3>
            <ul
              style={{
                fontSize: '15px',
                lineHeight: 1.8,
                color: '#666666',
                listStyleType: 'disc',
                textAlign: 'left',
                paddingLeft: '20px',
                maxWidth: '360px',
                margin: '0 auto',
              }}
            >
              <li>
                <strong>Cargo Total</strong> — Importe Total del Depósito — Si cancelas hasta 1 día antes de la fecha de llegada
              </li>
              <li>
                <strong>Cargo Parcial</strong> — 50% de Depósito — Si cancelas hasta 3 días antes de la fecha de llegada
              </li>
              <li>
                <strong>Sin costo</strong> — Si cancelas hasta 7 días antes de la fecha de llegada
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ─── Page ─── */
export function RoomsPage() {
  const { i18n } = useTranslation()
  const locale = i18n.language.startsWith('en') ? 'en' : 'es'
  const es = locale === 'es'

  return (
    <>
      <Helmet>
        <title>{`${es ? 'Habitaciones' : 'Rooms'} | ${SITE_NAME}`}</title>
        <meta
          name="description"
          content={
            es
              ? 'Habitaciones diseñadas para tu comodidad en Hotel Boutique Nueva Valladolid, Comayagua, Honduras.'
              : 'Rooms designed for your comfort at Hotel Boutique Nueva Valladolid, Comayagua, Honduras.'
          }
        />
      </Helmet>

      {/* ── Hero banner ── */}
      <section
        className="relative flex items-center justify-center"
        style={{
          backgroundImage: 'url(/images/nuva/rooms/room-sencilla-queen-hero.jpeg)',
          backgroundAttachment: 'fixed',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          paddingTop: '200px',
          paddingBottom: '200px',
          marginTop: '-80px',
        }}
      >
        {/* Overlay */}
        <div
          className="absolute inset-0"
          style={{ backgroundColor: 'rgba(0,0,0,0.30)' }}
        />
        <div className="relative z-10 text-center px-4">
          <h1
            className="font-display"
            style={{
              fontSize: '64px',
              fontWeight: 400,
              color: '#FFFFFF',
              lineHeight: 1.1,
              textShadow: '0px 0px 15px rgba(0,0,0,1)',
              marginBottom: '8px',
            }}
          >
            {es ? 'Habitaciones' : 'Rooms'}
          </h1>
          <h2
            className="font-heading"
            style={{
              fontSize: '18px',
              fontWeight: 700,
              textTransform: 'uppercase',
              color: '#F3EADB',
              marginTop: '-4px',
              letterSpacing: '0.12em',
            }}
          >
            Hotel Boutique Nueva Valladolid
          </h2>
        </div>
      </section>

      {/* ── Intro section ── */}
      <section style={{ backgroundColor: '#FFFFFF', paddingTop: '75px', paddingLeft: '40px', paddingRight: '40px', paddingBottom: '0' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', textAlign: 'center' }}>
          <h3
            className="font-heading"
            style={{
              fontSize: '23px',
              fontWeight: 700,
              textTransform: 'uppercase',
              color: '#0F2966',
              marginBottom: '20px',
            }}
          >
            {es ? 'Habitaciones Diseñadas para tu Comodidad' : 'Rooms Designed for Your Comfort'}
          </h3>
          <p
            style={{
              fontSize: '16px',
              lineHeight: 1.75,
              color: '#666666',
              maxWidth: '600px',
              margin: '0 auto',
            }}
          >
            {es
              ? 'Nuestras habitaciones están diseñadas para ofrecer un refugio sereno y productivo para los viajeros corporativos. Equipadas con comodidades modernas, áreas de trabajo privadas y un ambiente silencioso, garantizan el descanso y la concentración que necesitas. Ya sea que estés aquí para trabajar o relajarte, encontrarás en nuestras habitaciones el equilibrio perfecto entre confort y funcionalidad.'
              : 'Our rooms are designed to provide a serene and productive refuge for corporate travelers. Equipped with modern amenities, private work areas, and a quiet atmosphere, they guarantee the rest and focus you need. Whether you are here to work or relax, you will find in our rooms the perfect balance between comfort and functionality.'}
          </p>
        </div>
      </section>

      {/* ── Room detail rows (alternating white / navy) ── */}
      {ROOMS.map((room, index) => (
        <RoomRow key={room.key} room={room} index={index} />
      ))}

      {/* ── Policies ── */}
      <PoliciesSection />
    </>
  )
}
